import { useState, useRef, useEffect } from "react";
import LinkButton from "../shared/Buttons/LinkButton";
import TestimonialsSection from "../shared/TestimonialsSection/TestimonialsSection";
import {
  serviceOfferings,
  serviceDetails,
  testimonials,
} from "./data/consultingData";

const total = serviceOfferings.length;
const loopedItems = [...serviceOfferings, ...serviceOfferings, ...serviceOfferings];

const ConsultingServices = () => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isLooping = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / loopedItems.length;
    el.scrollLeft = cardWidth * total;
  }, []);

  const handleScroll = () => {
    if (isLooping.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / loopedItems.length;
    const rawIndex = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(rawIndex % total);
    if (rawIndex >= total * 2) {
      isLooping.current = true;
      el.scrollLeft -= cardWidth * total;
      requestAnimationFrame(() => { isLooping.current = false; });
    } else if (rawIndex < total) {
      isLooping.current = true;
      el.scrollLeft += cardWidth * total;
      requestAnimationFrame(() => { isLooping.current = false; });
    }
  };

  const scrollToIndex = (index) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / loopedItems.length;
    const currentRaw = Math.round(el.scrollLeft / cardWidth);
    const currentSet = Math.floor(currentRaw / total);
    el.scrollTo({ left: (currentSet * total + index) * cardWidth, behavior: "smooth" });
  };

  return (
    <div>
      {/* Header Section */}
      <section className="flex flex-col items-start w-full mb-16 relative">
        <div className="mt-24 md:mt-0 w-full h-[400px] md:h-[550px] lg:h-[650px] xl:h-[856px] md:overflow-hidden">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/pitbull-480.webp"
            srcSet="
              https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/pitbull-480.webp 480w,
              https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/pitbull-1280.webp 1280w,
              https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/pitbull-1920.webp 1920w
            "
            sizes="100vw"
            alt="A pitbull"
            className="w-full h-full object-cover md:object-[center_15%] lg:object-[center_20%] rounded-b-xl4"
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute bottom-16 left-8 md:left-14 lg:bottom-20 flex flex-col items-start gap-4 md:gap-8">
          <h1
            className="text-3xl md:text-5xl lg:text-6xl xl:text-8xl font-bold font-inter text-white text-center md:text-left"
            style={{ textShadow: "2px 2px 2px black" }}
          >
            <span className="block">Helping Animal</span>
            <span className="block">Nonprofits Grow</span>
          </h1>
          <LinkButton
            className="text-sm md:text-md lg:text-[24px] md:px-4 md:py-2 lg:px-6 lg:py-4"
            colour="orange"
            to="/contact-us"
          >
            Book a Free Consultation
          </LinkButton>
        </div>
      </section>

      {/* Running a rescue section */}
      <section className="flex flex-col items-center md:flex-row-reverse md:justify-center gap-16 md:my-16 xl:gap-24">
        <div className="flex flex-col items-center md:items-start">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/horizontal-line.svg"
            alt="horizontal line icon"
            className="w-32"
          />
          <h2 className="text-blue-dark1 text-3xl font-bold mt-8 mb-4 md:text-left xl:text-5xl">
            <span className="block">Running a rescue</span>
            <span className="block">shouldn’t mean</span>{" "}
            <span className="block">
              running on empty
            </span>
          </h2>
          <p className="md:text-left !md:text-xs md:w-64 md:leading-6 lg:w-92 xl:w-[500px]">
            Most rescues start with heart — but keeping them running takes
            strategy. Between donor fatigue, volunteer burnout, and endless
            fundraising, it’s easy to feel stuck.
          </p>
          <p className="font-bold my-2">That’s where we come in.</p>
        </div>
        <div className="flex flex-col items-center my-8">
          <div className="flex">
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dog-field.webp"
              alt="A dog running through flowers"
              className="w-48 md:w-56"
            />
            <div className="bg-orange h-20 w-20 xl:h-28 xl:w-28 xl:relative xl:bottom-8 rounded-full flex items-center justify-center">
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <div className="bg-blue h-16 w-16 xl:h-20 xl:w-20 rounded-full flex items-center justify-center">
            </div>
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dog-on-swing.webp"
              alt="A dog being lifted onto a swing"
              className="w-48 md:w-56"
            />
          </div>
        </div>
      </section>

      {/* What we offer section */}
      <section className="bg-blue/20 py-16 px-4 mt-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold text-blue-dark1 mb-4">
            What we offer
          </h2>
          <p className="text-sm md:text-base max-w-md mx-auto">
            Explore our main consulting pillars — each designed to help your
            rescue grow sustainably, save time & make a bigger impact.
          </p>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto pb-8 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {loopedItems.map((service, index) => (
            <div key={index} className="flex-none w-[280px] lg:w-[300px] xl:w-[320px] flex flex-col">
              <div className="rounded-2xl overflow-hidden shadow-consultingCardShadow flex flex-col flex-grow">
                <div className="h-40 xl:h-48 overflow-hidden shrink-0">
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`w-full h-full object-cover ${service.imageClass}`}
                  />
                </div>
                <div
                  className={`${service.bgColor} ${Math.floor((index % total) / 2) % 2 === 0 ? "text-black" : "text-white"} p-5 pb-6 flex flex-col flex-grow`}
                >
                  <img
                    src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/white-horizontal-line.svg"
                    alt="horizontal white line"
                    className="my-4 md:w-16 md:self-start"
                  />
                  <h3 className="font-bold text-lg md:text-xl lg:text-2xl xl:text-3xl mb-2 md:text-left h-[72px] overflow-hidden">
                    {service.title}
                  </h3>
                  <p className="text-sm xl:text-base mb-8 flex-grow md:text-left">
                    {service.description}
                  </p>
                  <LinkButton
                    to={service.link}
                    colour="white"
                    className="self-start !px-4 !py-2 !border-black !text-black"
                    onClick={(e) => service.link === "#" && e.preventDefault()}
                  >
                    Learn More
                  </LinkButton>
                </div>
              </div>
            </div>
          ))}
        </div>
        <ul className="flex justify-center gap-3 mt-8">
          {serviceOfferings.map((_, index) => (
            <li key={index} onClick={() => scrollToIndex(index)} className="cursor-pointer">
              <div className={`w-3 h-3 rounded-full bg-blue transition-opacity ${activeIndex === index ? "opacity-100" : "opacity-40"}`} />
            </li>
          ))}
        </ul>
      </section>

      {/* Get to know our services section */}
      <section className="px-4 md:px-8 lg:px-16 xl:px-24">
        <div className="flex flex-col gap-4 mb-12 max-w-7xl mx-auto my-12 lg:my-20">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/horizontal-line.svg"
            alt="Horizontal line"
            className="my-4 w-28 md:w-40 lg:w-56 mx-auto md:mx-0 md:self-start md:mb-8"
          />
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-bold text-blue text-center md:text-left">
              Get to know our services
            </h2>
            <p className="text-sm md:text-base text-gray-700 text-center md:text-left md:max-w-sm lg:max-w-md xl:max-w-lg">
              Each of our consulting areas below focuses on a different part of
              your nonprofit's growth — from fundraising & awareness to
              operations & digital strategy.
            </p>
          </div>
        </div>

        <div className="flex flex-col max-w-7xl mx-auto">
          {serviceDetails.map((service, index) => {
            const isOdd = index % 2 === 0; // This is to handle row reversing on medium screen sizes and above
            return (
              <div
                key={index}
                className={`flex flex-col ${isOdd ? "md:flex-row" : "md:flex-row-reverse"} min-h-[300px] lg:min-h-[400px]`}
              >
                <div
                  className={`${service.bgColor} text-black p-8 lg:p-12 xl:p-16 flex flex-col justify-center text-center md:text-left md:w-1/2`}
                >
                  <img
                    src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/white-horizontal-line.svg"
                    alt="horizontal line"
                    className="my-4 w-16 self-center md:self-start"
                  />
                  <h3 className="font-bold text-3xl lg:text-3xl xl:text-4xl">
                    {service.title}
                  </h3>
                  <h4 className="font-bold text-lg lg:text-base my-8">
                    {service.subheading}
                  </h4>
                  <p className="text-sm lg:text-base mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <LinkButton
                    to={service.link}
                    colour="orange"
                    className="self-center md:self-start px-4 py-2 !border-black !text-white"
                    onClick={(e) => service.link === "#" && e.preventDefault()} // Prevents infinit loop if there is no link url
                  >
                    Learn More
                  </LinkButton>
                </div>
                <div className="h-64 md:h-auto md:w-1/2 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`w-full h-full object-cover ${service.imageClass}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Ready to grow your rescue CTA */}
      <section className="relative w-full h-[300px] md:h-[350px] lg:h-[400px] overflow-hidden my-16">
        <div className="absolute inset-0 overflow-hidden xl:h-[600px]">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/golden-doodle.webp"
            alt="A happy golden doodle"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 flex flex-col justify-center items-center md:items-start text-center md:text-left px-8 md:px-16 lg:px-24">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white italic mb-4 [text-shadow:1px_1px_1px_black]"
          >
            Ready to grow
            <br />
            your rescue?
          </h2>
          <p
            className="text-white text-sm md:text-base mb-6 [text-shadow:1px_1px_1px_black]"
          >
            Let's plan your next
            <br />
            breakthrough!
          </p>
          <LinkButton className="px-6 py-2" colour="orange" to="/contact-us">
            Book a Free Consultation
          </LinkButton>
        </div>
      </section>

      <TestimonialsSection
        title={
          <>
            We Help Animal Nonprofits{" "}
            <span className="block text-orange font-extrabold text-6xl md:text-8xl">Thrive</span>
          </>
        }
        description="Every rescue deserves the tools to grow, sustain & save more lives. We're here to make that happen."
        testimonials={testimonials}
        images={[
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/cat-looking-up.webp", alt: "A cat looking up" },
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/tabby-cat.webp", alt: "An orange cat standing" },
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/pomeranian.webp", alt: "A pomeranian dog" },
        ]}
      />
    </div>
  );
};

export default ConsultingServices;
